import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BehaviorSubject } from 'rxjs';
import { Underwriter, UnderwritersColumns } from '../models/Underwriter';
import { UnderwriterDialogComponent } from '../underwriter-dialog/underwriter-dialog.component';
import { DeletePolicyUnderwriterDialogComponent } from '../../policies/policy-detail/tabs/policy-underwriters/components/delete-policy-underwriter-dialog/delete-policy-underwriter-dialog.component';
import { Logger } from '../../../infrastructure/logging/logger';
import { UnderwritersService } from '../services/underwriters.service';

@Component({
  selector: 'app-underwriters',
  templateUrl: './underwriters.component.html',
  styleUrls: ['./underwriters.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIcon,
    MatCard,
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    MatProgressSpinner,
    FormsModule
]
})
export class UnderwritersComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort;

  loading = new BehaviorSubject<boolean>(false);
  dataSource = new MatTableDataSource<Underwriter>;
  tableLength = 0;
  pageSize = 10;
  pageNumber: number = 1;
  displayedColumns = UnderwritersColumns;
  displayedColumnNames: string[] = [];
  searchKeyword: string = '';
  underwriters: Underwriter[] = [];
  mappedUnderwriters: any[] = [];

  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  constructor(
    private underwritersService: UnderwritersService,
    private logService: Logger,
    private dialog: MatDialog,
    private matSnackbar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.initTableColumns();
    this.loadPage();

    const storedItemsPerPage = localStorage.getItem('underwriterItemsPerPage');
    if (storedItemsPerPage) {
      this.initialPageSize.next(parseInt(storedItemsPerPage, 10));
    } else {
      this.initialPageSize.next(10);
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.initialPageSize?.subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.searchKeyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }
    this.displayedColumnNames = this.displayedColumns.map(c => c.column);
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.dataSource.data = [];

      this.underwriters = await this.underwritersService.getUnderwriters();
      this.mappedUnderwriters = this.underwriters.map((underwriter: any) => this.buildUnderwriterTableRow(underwriter));
      this.updateDataSource(this.mappedUnderwriters);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Underwriters', 'Error while retrieving Underwriters');
    } finally {
      this.loading.next(false);
    }
  }

  buildUnderwriterTableRow(underwriter: any): Underwriter {
    return {
      id: underwriter.id,
      name: underwriter.name,
      description: underwriter.description
    }
  }

  updateDataSource(underwriters: Underwriter[]) {
    this.dataSource.data = underwriters;
    this.tableLength = underwriters.length;
  }

  onNewUnderwriterClick(): void {
    this.openUnderwriterDialog();
  }

  openUnderwriterDialog(underwriter?: Underwriter) {
    const dialogRef = this.dialog.open(UnderwriterDialogComponent, {
      disableClose: true,
      width: '50%',
      data: underwriter
    });
    dialogRef.afterClosed().subscribe((payload) => {
      if (payload) {
        if (underwriter != undefined) {
          this.underwritersService.updateUnderwriter(underwriter.id, payload).then(_ => {
            this.loadPage();
          });
        } else {
          this.underwritersService.createUnderwriter(payload).then(_ => {
            this.loadPage();
          });
        }
      }
    });
  }

  onOpenEditUnderwriterDialog(row: any) {
    this.openUnderwriterDialog(row);
  }

  onOpenDeleteUnderwriterDialog(row: any) {
    this.openDeleteUnderwriterDialog(row);
  }

  openDeleteUnderwriterDialog(underwriter: Underwriter) {
    const deleteUnderwriterDialogRef = this.dialog.open(DeletePolicyUnderwriterDialogComponent, {
      width: '50vw',
      height: '250px',
      disableClose: false,
      data: 'Underwriter'
    });
    deleteUnderwriterDialogRef.afterClosed().subscribe(deleteBoolean => {
      if (deleteBoolean) {
        let payload = {
          id: underwriter.id,
          name: underwriter.name,
          description: underwriter.description,
          is_active: 0
        }
        this.underwritersService.updateUnderwriter(underwriter.id, payload).then(_ => {
          this.loadPage();
        });
      }
    });
  }

  applySearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  clearSearch() {
    this.searchKeyword = '';
    this.dataSource = new MatTableDataSource(this.underwriters);
    this.ngAfterViewInit();
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.matSnackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    localStorage.setItem('underwriterItemsPerPage', event.pageSize.toString());
  }
}